import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

export interface AnalyticsCardProps {
  id: string;
  title: string;
  value: number;
  comparison?: string;
  formatType?: string;
  infoTooltip?: string;
  clickable?: boolean;
  onClick?: () => void;
}

export const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  value,
  comparison,
  infoTooltip,
  clickable,
  onClick,
}) => {
  const isPositive = comparison?.startsWith('+');
  const isNegative = comparison?.startsWith('-');

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 p-4 min-h-[140px] flex flex-col justify-between ${
        clickable ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition-shadow' : ''
      }`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      {/* Header with title and info icon */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-gray-600 text-sm font-medium">{title}</h4>
        <svg
          className="w-3.5 h-3.5 text-gray-400 cursor-pointer flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {infoTooltip && <title>{infoTooltip}</title>}
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* Main value and comparison */}
      <div>
        <p className="text-3xl font-bold text-gray-900 mb-1">
          {value.toLocaleString()}
        </p>

        {/* Comparison text with arrow indicator */}
        {comparison && (
          <div className="flex items-center gap-1">
            {isPositive && (
              <ArrowUp className="w-3.5 h-3.5 text-green-600" />
            )}
            {isNegative && (
              <ArrowDown className="w-3.5 h-3.5 text-red-600" />
            )}
            <span className={`text-xs font-medium ${
              isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
            }`}>
              {comparison}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
